import { useState } from 'react';
import { sendAdminEmail } from '@/utils/emailService';

interface ImplementationGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ImplementationGuideModal({ isOpen, onClose }: ImplementationGuideModalProps) {
  const [formData, setFormData] = useState({
    schoolName: '',
    principalName: '',
    email: '',
    phone: '',
    board: '',
    studentStrength: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const body = new URLSearchParams({
        schoolName: formData.schoolName,
        principalName: formData.principalName,
        email: formData.email,
        phone: formData.phone,
        board: formData.board,
        studentStrength: formData.studentStrength,
      });

      const res = await fetch('https://readdy.ai/api/form/d78h4lr86jhav3jpf840', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        // Send admin notification email
        await sendAdminEmail({
          subject: `Implementation Guide Download - ${formData.schoolName}`,
          formType: 'Implementation Guide Download',
          replyTo: formData.email,
          fields: {
            'School Name': formData.schoolName,
            'Principal Name': formData.principalName,
            'Email': formData.email,
            'Phone': formData.phone,
            'Board': formData.board,
            'Student Strength': formData.studentStrength,
          },
        });
      }
    } catch {
      // Silent fail — still show success to user
    } finally {
      setIsSubmitting(false);
      setIsSuccess(true);

      // Trigger PDF download
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = '/implementation-guide.pdf';
        link.download = 'STEMForge-Implementation-Guide.pdf';
        link.click();

        setTimeout(() => {
          onClose();
          setIsSuccess(false);
          setFormData({
            schoolName: '',
            principalName: '',
            email: '',
            phone: '',
            board: '',
            studentStrength: '',
          });
        }, 2000);
      }, 1000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all cursor-pointer"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        <div className="p-8">
          {!isSuccess ? (
            <>
              <div className="mb-8">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4">
                  <i className="ri-file-download-line text-3xl text-white"></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-3">
                  Download Implementation Guide
                </h2>
                <p className="text-gray-600 text-lg">
                  Get our comprehensive 50+ page guide covering complete STEM lab setup, curriculum integration, and implementation roadmap.
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 mb-8">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-file-text-line text-blue-600"></i>
                  What's Inside:
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5"></i>
                    <span>Complete 12-month implementation timeline</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5"></i>
                    <span>Lab setup specifications & equipment list</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5"></i>
                    <span>Curriculum mapping for CBSE/ICSE/State boards</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5"></i>
                    <span>Teacher training modules & certification paths</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5"></i>
                    <span>Student assessment frameworks & rubrics</span>
                  </li>
                </ul>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      School Name *
                    </label>
                    <input
                      type="text"
                      name="schoolName"
                      value={formData.schoolName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter school name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Principal Name *
                    </label>
                    <input
                      type="text"
                      name="principalName"
                      value={formData.principalName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter your name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="principal@school.edu"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="+91 7826019010"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Board Affiliation *
                    </label>
                    <select
                      name="board"
                      value={formData.board}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">Select board</option>
                      <option value="CBSE">CBSE</option>
                      <option value="ICSE">ICSE</option>
                      <option value="State Board">State Board</option>
                      <option value="IB">IB</option>
                      <option value="IGCSE">IGCSE</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Student Strength *
                    </label>
                    <select
                      name="studentStrength"
                      value={formData.studentStrength}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">Select range</option>
                      <option value="Under 500">Under 500</option>
                      <option value="500-1000">500-1000</option>
                      <option value="1000-2000">1000-2000</option>
                      <option value="2000+">2000+</option>
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-download-cloud-line text-xl"></i>
                      Download Guide Now
                    </span>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  By downloading, you agree to receive updates about STEMForge programs
                </p>
              </form>
            </>
          ) : (
            <div className="py-12 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
                <i className="ri-check-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Download Starting!
              </h3>
              <p className="text-gray-600 mb-6">
                Your implementation guide is being downloaded. Check your downloads folder.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <i className="ri-mail-line"></i>
                <span>A copy has also been sent to your email</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
